'use client';

import React from 'react';
import { useRouter, usePathname } from 'next/navigation';
import { useTranslations } from '@/hooks/useTranslations';
import { useLocale } from '@/contexts/LocaleContext';
import { getPathForLocale } from '@/lib/i18n';
import { getToken } from '@/lib/auth';
import { getAutoExternalRel } from '@/lib/link-rel';
import { getGameImageCandidates } from '@/lib/game-image';
import { SafeGameImage } from '@/components/SafeGameImage';

function StarRating({ rating, max = 5, size = 'sm' }: { rating: number; max?: number; size?: 'sm' | 'md' }) {
  const s = size === 'md' ? 'h-5 w-5' : 'h-4 w-4';
  return (
    <span className="inline-flex text-[#ffb74d]" aria-label={`${rating} of ${max} stars`}>
      {Array.from({ length: max }, (_, i) => (
        <svg
          key={i}
          className={`${s} shrink-0 ${i < Math.round(rating) ? 'fill-current' : 'fill-none opacity-40'}`}
          viewBox="0 0 24 24"
          stroke="currentColor"
          strokeWidth={1.5}
        >
          <path d="M12 2.8l2.9 6 6.6.9-4.8 4.6 1.2 6.6L12 17.8 6.1 20.9l1.2-6.6L2.5 9.7l6.6-.9L12 2.8Z" />
        </svg>
      ))}
    </span>
  );
}

const REVIEWS_PER_PAGE = 5;

function formatTimeAgo(isoDate: string, locale: string): string {
  const date = new Date(isoDate);
  if (Number.isNaN(date.getTime())) return '';
  const now = new Date();
  const diffMs = now.getTime() - date.getTime();
  const diffSec = Math.floor(diffMs / 1000);
  const diffMin = Math.floor(diffSec / 60);
  const diffHour = Math.floor(diffMin / 60);
  const diffDay = Math.floor(diffHour / 24);
  const diffWeek = Math.floor(diffDay / 7);
  const diffMonth = Math.floor(diffDay / 30);
  const diffYear = Math.floor(diffDay / 365);

  const rtf = new Intl.RelativeTimeFormat(locale, { numeric: 'always' });
  if (diffSec < 60) return rtf.format(-diffSec, 'second');
  if (diffMin < 60) return rtf.format(-diffMin, 'minute');
  if (diffHour < 24) return rtf.format(-diffHour, 'hour');
  if (diffDay < 7) return rtf.format(-diffDay, 'day');
  if (diffWeek < 4) return rtf.format(-diffWeek, 'week');
  if (diffMonth < 12) return rtf.format(-diffMonth, 'month');
  return rtf.format(-diffYear, 'year');
}

function GamePreloader({ loadingText }: { loadingText: string }) {
  return (
    <div className="absolute inset-0 z-20 flex flex-col items-center justify-center gap-4 rounded-t-2xl bg-[#0f111a]/85 backdrop-blur-sm" aria-hidden>
      <div className="pointer-events-none absolute inset-0">
        <div className="absolute -left-10 top-12 h-40 w-40 rounded-full bg-[#64b5f6]/15 blur-3xl" />
        <div className="absolute right-8 top-8 h-32 w-32 rounded-full bg-[#ffb74d]/15 blur-2xl" />
        <div className="absolute bottom-8 left-1/2 h-28 w-28 -translate-x-1/2 rounded-full bg-fuchsia-500/10 blur-2xl" />
      </div>
      <div className="h-12 w-12 animate-spin rounded-full border-2 border-white/20 border-t-[#ffb74d]" />
      <span className="text-sm text-slate-400">{loadingText}</span>
    </div>
  );
}

function GameIdleBackdrop({ gameTitle }: { gameTitle: string }) {
  return (
    <div className="pointer-events-none absolute inset-0 overflow-hidden" aria-hidden>
      <div className="absolute inset-0 bg-[radial-gradient(circle_at_15%_20%,rgba(100,181,246,0.24),transparent_34%),radial-gradient(circle_at_82%_16%,rgba(255,183,77,0.22),transparent_33%),radial-gradient(circle_at_48%_92%,rgba(217,70,239,0.18),transparent_35%)]" />
      <div className="absolute inset-0 bg-[linear-gradient(120deg,rgba(255,255,255,0.03)_0%,rgba(255,255,255,0)_45%)]" />
      <div className="absolute left-6 top-6 rounded-xl border border-white/10 bg-[#1e2235]/75 px-3 py-1.5 text-[11px] font-semibold uppercase tracking-wider text-[#ffb74d] shadow-[0_0_24px_rgba(255,183,77,0.18)]">
        Demo Mode
      </div>
      <div className="absolute right-6 top-6 rounded-xl border border-white/10 bg-[#1e2235]/70 px-3 py-1.5 text-[11px] font-semibold text-slate-300">
        RTP UI
      </div>
      <div className="absolute bottom-6 left-6 rounded-xl border border-white/10 bg-[#1e2235]/70 px-3 py-2 text-xs text-slate-300">
        <div className="font-semibold text-white">HotlistCasino</div>
        <div className="text-[11px] text-slate-400">{gameTitle}</div>
      </div>
      <div className="absolute right-6 bottom-6 flex gap-2">
        {['777', 'WILD', 'BONUS'].map((chip) => (
          <span
            key={chip}
            className="rounded-full border border-white/15 bg-white/5 px-2.5 py-1 text-[10px] font-bold tracking-wide text-slate-200"
          >
            {chip}
          </span>
        ))}
      </div>
    </div>
  );
}

type GameReview = {
  id: string;
  rating: number;
  text: string;
  createdAt: string;
  authorName: string;
  avatarUrl: string | null;
};

export function GamePageClient({
  demoUrl,
  gameSlug,
  providerSlug,
  gameTitle,
  gameImageUrl,
  playAtCasinoUrl,
  isComingSoon = false,
}: {
  demoUrl: string;
  gameSlug: string;
  providerSlug: string;
  gameTitle: string;
  gameImageUrl?: string;
  playAtCasinoUrl?: string;
  isComingSoon?: boolean;
}) {
  const { t } = useTranslations();
  const locale = useLocale();
  const router = useRouter();
  const pathname = usePathname();
  const [playingDemo, setPlayingDemo] = React.useState(false);
  const [fullscreenOpen, setFullscreenOpen] = React.useState(false);
  const [iframeLoading, setIframeLoading] = React.useState(false);
  const fullscreenContainerRef = React.useRef<HTMLDivElement>(null);
  const [reviewPopupOpen, setReviewPopupOpen] = React.useState(false);
  const [reviewRating, setReviewRating] = React.useState(5);
  const [reviewText, setReviewText] = React.useState('');
  const [ratingDropdownOpen, setRatingDropdownOpen] = React.useState(false);
  const ratingDropdownRef = React.useRef<HTMLDivElement>(null);
  const [submittingReview, setSubmittingReview] = React.useState(false);
  const [reviewError, setReviewError] = React.useState<string | null>(null);
  const [reviewSuccess, setReviewSuccess] = React.useState(false);
  const [reviewsData, setReviewsData] = React.useState<{
    items: GameReview[];
    averageRating: number;
    count: number;
  } | null>(null);
  const [userHasReviewed, setUserHasReviewed] = React.useState<boolean | null>(null);
  const [reviewsPage, setReviewsPage] = React.useState(1);
  const resolvedPlayAtCasinoUrl = String(playAtCasinoUrl ?? '').trim() || 'https://hotlistcasino.com';

  const loadReviews = React.useCallback(async () => {
    try {
      const res = await fetch(
        `/api/game-reviews?gameSlug=${encodeURIComponent(gameSlug)}`,
      );
      if (res.ok) {
        const data = (await res.json()) as { items: GameReview[]; averageRating: number; count: number };
        setReviewsData(data);
      }
    } catch {
      // ignore
    }
  }, [gameSlug]);

  React.useEffect(() => {
    loadReviews();
  }, [loadReviews]);

  React.useEffect(() => {
    if (!fullscreenOpen) return;
    // Blur the button that triggered fullscreen so Space/Enter (which activate
    // the focused button) are not intercepted and instead reach the game iframe.
    if (document.activeElement instanceof HTMLElement) {
      document.activeElement.blur();
    }
    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') setFullscreenOpen(false);
    };
    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    document.addEventListener('keydown', handleKeyDown);
    return () => {
      document.body.style.overflow = previousOverflow;
      document.removeEventListener('keydown', handleKeyDown);
    };
  }, [fullscreenOpen]);

  // A `position: fixed` element is positioned relative to the nearest ancestor
  // with a transform/filter/perspective (the containing block), not the viewport.
  // The game card sits inside MotionReveal which applies a transform, so fullscreen
  // would be clipped/offset. Also, ancestors with positioned + z-index create
  // stacking contexts (e.g. <main class="relative z-10">) that cap the fullscreen
  // z-index below the site header. Neutralize both while fullscreen and restore on exit.
  React.useEffect(() => {
    if (!fullscreenOpen) return;
    const el = fullscreenContainerRef.current;
    if (!el) return;
    const overrides: Array<{ el: HTMLElement; prop: string; saved: string }> = [];
    const containingProps = ['transform', 'filter', 'perspective'] as const;
    let node = el.parentElement as HTMLElement | null;
    while (node && node !== document.documentElement) {
      const cs = getComputedStyle(node);
      for (const prop of containingProps) {
        const value = cs[prop];
        if (value && value !== 'none') {
          overrides.push({ el: node, prop, saved: node.style.getPropertyValue(prop) });
          node.style.setProperty(prop, 'none', 'important');
        }
      }
      const willChange = cs.willChange;
      if (willChange && willChange !== 'auto' && /transform|perspective/.test(willChange)) {
        overrides.push({ el: node, prop: 'will-change', saved: node.style.getPropertyValue('will-change') });
        node.style.setProperty('will-change', 'auto', 'important');
      }
      // Break stacking contexts so the fullscreen z-index is compared at the root level
      // (above the sticky header) instead of being capped by an ancestor's z-index.
      if (cs.position !== 'static' && cs.zIndex !== 'auto') {
        overrides.push({ el: node, prop: 'z-index', saved: node.style.getPropertyValue('z-index') });
        node.style.setProperty('z-index', 'auto', 'important');
      }
      node = node.parentElement as HTMLElement | null;
    }
    return () => {
      for (const { el: n, prop, saved } of overrides) {
        if (saved) n.style.setProperty(prop, saved, 'important');
        else n.style.removeProperty(prop);
      }
    };
  }, [fullscreenOpen]);

  React.useEffect(() => {
    setReviewsPage(1);
  }, [reviewsData?.items?.length]);

  const loadUserGameReviews = React.useCallback(async () => {
    const token = getToken();
    if (!token) {
      setUserHasReviewed(false);
      return;
    }
    try {
      const res = await fetch('/api/game-reviews/me', {
        headers: { Authorization: `Bearer ${token}` },
        cache: 'no-store',
      });
      if (res.ok) {
        const items = (await res.json()) as Array<{ gameSlug?: string }>;
        const hasReviewed = Array.isArray(items) && items.some((r) => String(r?.gameSlug ?? '').trim() === gameSlug);
        setUserHasReviewed(hasReviewed);
      } else {
        setUserHasReviewed(false);
      }
    } catch {
      setUserHasReviewed(false);
    }
  }, [gameSlug]);

  React.useEffect(() => {
    loadUserGameReviews();
    const handler = () => loadUserGameReviews();
    window.addEventListener('auth:token', handler);
    return () => window.removeEventListener('auth:token', handler);
  }, [loadUserGameReviews]);

  const handleWriteCommentClick = () => {
    if (userHasReviewed) return;
    setReviewError(null);
    setReviewSuccess(false);
    const token = getToken();
    if (!token) {
      // Strip locale prefix for next param so login redirect works correctly
      const pathWithoutLocale = pathname?.replace(/^\/(en|ru|fr|es)/, '') || '';
      const nextPath = pathWithoutLocale.startsWith('/') ? pathWithoutLocale : pathWithoutLocale ? `/${pathWithoutLocale}` : `/games/${gameSlug}`;
      router.push(`${getPathForLocale(locale, '/login')}?next=${encodeURIComponent(nextPath)}`);
      return;
    }
    setReviewPopupOpen(true);
  };

  React.useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      const target = e.target as Node;
      if (!ratingDropdownRef.current?.contains(target)) {
        setRatingDropdownOpen(false);
      }
    };
    if (ratingDropdownOpen) {
      document.addEventListener('click', handleClickOutside, { capture: false });
    }
    return () => document.removeEventListener('click', handleClickOutside, { capture: false });
  }, [ratingDropdownOpen]);

  const handleSubmitReview = async (e: React.FormEvent) => {
    e.preventDefault();
    setRatingDropdownOpen(false);
    const token = getToken();
    if (!token) {
      setReviewError(t('games.reviewErrorSignIn'));
      return;
    }
    const text = reviewText.trim();
    if (text.length < 10) {
      setReviewError(t('games.reviewErrorMinLength'));
      return;
    }
    setReviewError(null);
    setSubmittingReview(true);
    try {
      const res = await fetch('/api/game-reviews', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({
          gameSlug,
          rating: reviewRating,
          text,
        }),
      });
      const data = (await res.json().catch(() => ({}))) as { message?: string };
      if (res.ok) {
        setReviewPopupOpen(false);
        setReviewText('');
        setReviewRating(5);
        setReviewSuccess(true);
        await loadReviews();
        await loadUserGameReviews();
      } else {
        setReviewError(data.message || t('games.reviewErrorGeneric'));
      }
    } catch {
      setReviewError(t('games.reviewErrorNetwork'));
    } finally {
      setSubmittingReview(false);
    }
  };

  return (
    <>
      <div className="flex min-h-[420px] flex-col rounded-2xl border border-white/5 bg-[#0f111a] overflow-hidden">
        {/* Game area: CTA or iframe */}
        <div className="relative flex flex-1 min-h-[360px] flex-col">
          <div className="pointer-events-none absolute inset-0 z-0 overflow-hidden rounded-t-2xl" aria-hidden>
            <SafeGameImage
              src={gameImageUrl}
              srcCandidates={getGameImageCandidates(providerSlug, gameSlug, 'hero')}
              alt=""
              imageClassName="absolute inset-0 h-full w-full scale-110 object-cover object-center blur-[4px]"
              placeholderTitle={gameTitle}
              placeholderContainerClassName="absolute inset-0"
            />
            <div className="absolute inset-0 bg-[#0f111a]/85" />
          </div>
          {playingDemo ? (
            <>
              {iframeLoading && <GamePreloader loadingText={t('games.loadingGame')} />}
              {/* The iframe stays mounted in the same DOM node for both inline and
                  fullscreen states; only CSS changes, so the game never reloads. */}
              <div
                ref={fullscreenContainerRef}
                className={
                  fullscreenOpen
                    ? 'fixed inset-0 z-[10000] flex flex-col bg-black'
                    : 'absolute inset-0 overflow-hidden rounded-t-2xl'
                }
                role={fullscreenOpen ? 'dialog' : undefined}
                aria-modal={fullscreenOpen || undefined}
                aria-label={fullscreenOpen ? 'Game fullscreen' : undefined}
              >
                <div className="absolute right-3 top-3 z-20 flex items-stretch gap-2">
                  {fullscreenOpen ? (
                    <button
                      type="button"
                      onClick={() => setFullscreenOpen(false)}
                      className="flex h-10 w-10 shrink-0 items-center justify-center rounded-lg border border-white/20 bg-black/60 text-white backdrop-blur-sm hover:bg-white/10"
                      title={t('games.exitFullscreen')}
                      aria-label={t('games.exitFullscreen')}
                    >
                      <svg className="h-5 w-5 shrink-0" fill="none" stroke="currentColor" strokeWidth={2} viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" d="M4 8V4m0 0h4M20 16v4m0 0h-4M16 4h4v4M8 20H4v-4" />
                      </svg>
                    </button>
                  ) : (
                    <>
                      <button
                        type="button"
                        onClick={() => setFullscreenOpen(true)}
                        className="flex h-10 w-10 shrink-0 items-center justify-center rounded-lg border border-white/20 bg-black/60 text-white backdrop-blur-sm hover:bg-white/10"
                        title={t('games.openFullscreen')}
                        aria-label={t('games.openFullscreen')}
                      >
                        <svg className="h-5 w-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 8V4m0 0h4M4 4l5 5m11-1V4m0 0h-4m4 0l-5 5M4 16v4m0 0h4m-4 0l5-5m11 5l-5-5m5 5v-4m0 4h-4" />
                        </svg>
                      </button>
                      <button
                        type="button"
                        onClick={() => setPlayingDemo(false)}
                        className="flex h-10 items-center justify-center rounded-lg border border-white/20 bg-black/60 px-4 text-sm text-white backdrop-blur-sm hover:bg-white/10"
                      >
                        {t('games.closeDemo')}
                      </button>
                    </>
                  )}
                </div>
                <iframe
                  src={demoUrl}
                  title="Game demo"
                  className={`h-full w-full border-0 ${fullscreenOpen ? 'flex-1' : ''}`}
                  allow="fullscreen; autoplay; encrypted-media"
                  allowFullScreen
                  onLoad={() => setIframeLoading(false)}
                />
              </div>
          </>
          ) : (
            <>
              <GameIdleBackdrop gameTitle={gameTitle} />
              <div className="absolute inset-0 bg-gradient-to-b from-transparent via-[#0f111a]/55 to-[#0f111a]" />
              <div className="relative z-10 flex flex-1 flex-col items-center justify-center p-8">
                <div className="w-full max-w-md rounded-2xl border border-white/10 bg-[#1e2235]/90 p-8 backdrop-blur-md">
                  <a
                    href={resolvedPlayAtCasinoUrl}
                    target="_blank"
                    rel={getAutoExternalRel(resolvedPlayAtCasinoUrl)}
                    className="mb-4 flex w-full items-center justify-center rounded-lg bg-gradient-to-r from-amber-500 to-orange-500 py-4 font-bold text-black shadow-lg transition-all hover:-translate-y-0.5 hover:from-amber-400 hover:to-orange-400"
                  >
                    {t('games.playAtCasino')}
                  </a>
                  {!isComingSoon && (
                  <button
                    type="button"
                    onClick={() => {
                      setPlayingDemo(true);
                      setIframeLoading(true);
                    }}
                    className="flex w-full items-center justify-center rounded-lg border-2 border-white/20 py-3.5 font-bold text-white transition-colors hover:border-white/40 hover:bg-white/5"
                  >
                    {t('games.playDemo')}
                  </button>
                  )}
                  <p className="mt-4 text-center text-xs text-slate-500">18+ | {t('footer.playResponsibly')}</p>
                </div>
              </div>
            </>
          )}
        </div>
        <div className="border-t border-white/5 bg-[#161925] px-6 py-4">
          <div className="flex flex-wrap items-center justify-between gap-4">
            <div className="flex items-center gap-3">
              <span className="text-sm text-slate-400">{t('games.userRating')}</span>
              {reviewsData && reviewsData.count > 0 ? (
                <span className="flex items-center gap-2">
                  <StarRating rating={reviewsData.averageRating} size="md" />
                  <span className="text-sm font-medium text-white">
                    {reviewsData.averageRating.toFixed(1)} ({reviewsData.count})
                  </span>
                </span>
              ) : (
                <span className="text-sm font-medium text-white">—</span>
              )}
            </div>
            <button
              type="button"
              onClick={handleWriteCommentClick}
              disabled={userHasReviewed === true}
              className={`rounded-full px-4 py-1.5 text-sm transition-colors ${
                userHasReviewed
                  ? 'cursor-not-allowed border border-white/10 text-slate-500'
                  : 'border border-emerald-400/30 text-emerald-400 hover:bg-emerald-400/10'
              }`}
            >
              {userHasReviewed ? t('games.alreadyReviewed') : t('games.writeComment')}
            </button>
          </div>
          {reviewSuccess && (
            <p className="mt-4 rounded-lg bg-emerald-500/20 px-4 py-2 text-sm text-emerald-200">
              {t('games.thanksPending')}
            </p>
          )}
          {reviewsData && reviewsData.items.length > 0 && (
            <div className="mt-4 space-y-3">
              {reviewsData.items.slice(0, 3).map((r) => (
                <div
                  key={r.id}
                  className="flex gap-3 rounded-lg border border-white/5 bg-white/5 px-3 py-2"
                >
                  <div className="flex h-8 w-8 shrink-0 items-center justify-center overflow-hidden rounded-full bg-[#ffb74d]/20 text-xs font-bold text-[#ffb74d]">
                    {r.avatarUrl ? (
                      /* eslint-disable-next-line @next/next/no-img-element */
                      <img src={r.avatarUrl} alt="" className="h-full w-full object-cover" />
                    ) : (
                      (r.authorName || '?').charAt(0).toUpperCase()
                    )}
                  </div>
                  <div className="min-w-0 flex-1">
                    <div className="flex flex-wrap items-center gap-2">
                      <span className="text-sm font-medium text-white">{r.authorName}</span>
                      <StarRating rating={r.rating} size="sm" />
                      <span className="text-[10px] text-slate-500">{formatTimeAgo(r.createdAt, locale)}</span>
                    </div>
                    <p className="mt-0.5 text-xs text-slate-400 line-clamp-2">{r.text}</p>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>

      {/* Dedicated Reviews section */}
      <section className="mt-8 rounded-2xl border border-white/10 bg-[#161925] p-6" aria-labelledby="game-reviews-heading">
        <h2 id="game-reviews-heading" className="mb-4 text-xl font-bold text-white">
          {t('games.reviewsSection')}
        </h2>
        {reviewsData && reviewsData.items.length > 0 ? (
          <>
            <div className="space-y-4">
              {reviewsData.items
                .slice((reviewsPage - 1) * REVIEWS_PER_PAGE, reviewsPage * REVIEWS_PER_PAGE)
                .map((r) => (
                  <div
                    key={r.id}
                    className="flex gap-4 rounded-xl border border-white/5 bg-white/5 px-4 py-3"
                  >
                    <div className="flex h-10 w-10 shrink-0 items-center justify-center overflow-hidden rounded-full bg-[#ffb74d]/20 text-sm font-bold text-[#ffb74d]">
                      {r.avatarUrl ? (
                        /* eslint-disable-next-line @next/next/no-img-element */
                        <img src={r.avatarUrl} alt="" className="h-full w-full object-cover" />
                      ) : (
                        (r.authorName || '?').charAt(0).toUpperCase()
                      )}
                    </div>
                    <div className="min-w-0 flex-1">
                      <div className="flex flex-wrap items-center gap-2">
                        <span className="text-sm font-medium text-white">{r.authorName}</span>
                        <StarRating rating={r.rating} size="sm" />
                        <span className="text-xs text-slate-500" title={r.createdAt}>
                          {formatTimeAgo(r.createdAt, locale)}
                        </span>
                      </div>
                      <p className="mt-2 text-sm text-slate-300">{r.text}</p>
                    </div>
                  </div>
                ))}
            </div>
            {reviewsData.items.length > REVIEWS_PER_PAGE && (
              <div className="mt-4 flex items-center justify-between border-t border-white/5 pt-4">
                <button
                  type="button"
                  onClick={() => setReviewsPage((p) => Math.max(1, p - 1))}
                  disabled={reviewsPage <= 1}
                  className="rounded-lg border border-white/10 px-4 py-2 text-sm text-slate-300 transition-colors hover:bg-white/5 disabled:cursor-not-allowed disabled:opacity-50"
                >
                  {t('pagination.previous')}
                </button>
                <span className="text-sm text-slate-500">
                  {reviewsPage} / {Math.ceil(reviewsData.items.length / REVIEWS_PER_PAGE)}
                </span>
                <button
                  type="button"
                  onClick={() =>
                    setReviewsPage((p) =>
                      Math.min(Math.ceil(reviewsData.items.length / REVIEWS_PER_PAGE), p + 1),
                    )
                  }
                  disabled={reviewsPage >= Math.ceil(reviewsData.items.length / REVIEWS_PER_PAGE)}
                  className="rounded-lg border border-white/10 px-4 py-2 text-sm text-slate-300 transition-colors hover:bg-white/5 disabled:cursor-not-allowed disabled:opacity-50"
                >
                  {t('pagination.next')}
                </button>
              </div>
            )}
          </>
        ) : (
          <p className="text-sm text-slate-500">{t('games.noReviewsYet')}</p>
        )}
      </section>

      {reviewPopupOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-[#0b0d19]/70 p-4">
          <div className="w-full max-w-md rounded-2xl border border-white/10 bg-[#1e2235] p-6 shadow-xl">
            <h3 className="text-lg font-semibold text-white">{t('games.writeComment')}</h3>
            <p className="mt-1 text-sm text-slate-400">{gameTitle}</p>
            <form onSubmit={handleSubmitReview} className="mt-4 space-y-4">
              <div ref={ratingDropdownRef} className="relative">
                <label className="text-xs uppercase tracking-wider text-slate-400">{t('games.reviewRating')}</label>
                <button
                  type="button"
                  onClick={() => setRatingDropdownOpen((o) => !o)}
                  className="mt-2 flex w-full items-center justify-between rounded-full border border-white/10 bg-[#15192b] px-4 py-2 text-sm text-slate-200"
                >
                  <span>
                    {reviewRating} {t('games.stars')}
                  </span>
                  <svg
                    className={`h-4 w-4 text-slate-500 transition-transform ${ratingDropdownOpen ? 'rotate-180' : ''}`}
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                  </svg>
                </button>
                {ratingDropdownOpen && (
                  <div className="absolute left-0 right-0 top-full z-10 mt-1 rounded-xl border border-white/10 bg-[#15192b] py-1 shadow-xl">
                    {[5, 4, 3, 2, 1].map((v) => (
                      <button
                        key={v}
                        type="button"
                        onClick={() => {
                          setReviewRating(v);
                          setRatingDropdownOpen(false);
                        }}
                        className={`flex w-full items-center gap-2 px-4 py-2 text-left text-sm transition-colors hover:bg-white/10 ${
                          v === reviewRating ? 'bg-[#ffb74d]/20 text-[#ffb74d]' : 'text-slate-200'
                        }`}
                      >
                        <StarRating rating={v} size="sm" />
                        {v} {t('games.stars')}
                      </button>
                    ))}
                  </div>
                )}
              </div>
              <div>
                <label className="text-xs uppercase tracking-wider text-slate-400">{t('games.reviewText')}</label>
                <textarea
                  required
                  minLength={10}
                  className="mt-2 min-h-[100px] w-full rounded-2xl border border-white/10 bg-[#15192b] px-4 py-3 text-sm text-slate-200 placeholder:text-slate-500"
                  placeholder={t('games.reviewPlaceholder')}
                  value={reviewText}
                  onChange={(e) => setReviewText(e.target.value)}
                />
              </div>
              {reviewError && (
                <p className="rounded-lg bg-red-500/20 px-4 py-2 text-sm text-red-300">{reviewError}</p>
              )}
              <div className="flex flex-wrap justify-end gap-3">
                <button
                  type="button"
                  className="rounded-full border border-white/10 px-4 py-2 text-sm text-slate-200 hover:bg-white/10"
                  onClick={() => setReviewPopupOpen(false)}
                  disabled={submittingReview}
                >
                  {t('account.removeGameCancel')}
                </button>
                <button
                  type="submit"
                  className="rounded-full bg-emerald-600 px-4 py-2 text-sm font-semibold text-white hover:bg-emerald-500 disabled:opacity-60"
                  disabled={submittingReview}
                >
                  {submittingReview ? t('games.submittingReview') : t('games.submitReview')}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </>
  );
}
